import { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { MessageSquare, Check, Smile } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useOnboardingDataStore } from '@/stores/onboardingDataStore';
import { upsertDescriptionPreferences } from '@/services/data/planningPreferences';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { ToneOfVoice } from '@/types/scheduling';

interface DescriptionPreferencesStepProps {
  onComplete: () => void;
  setLoading: (loading: boolean) => void;
  onCanProceedChange?: (canProceed: boolean) => void;
  onPreferencesChange?: (preferences: {
    toneOfVoice: ToneOfVoice;
    hasRecurringCta: boolean;
    recurringCtaText: string;
    hasEmojis: boolean;
  }) => void;
}

export interface DescriptionPreferencesStepRef {
  handleContinue: () => void;
}

const TONE_OPTIONS: { value: ToneOfVoice; label: string; description: string }[] = [
  {
    value: 'vous',
    label: '"Vous"',
    description: 'Ton formel et professionnel',
  },
  {
    value: 'tu',
    label: '"Tu"',
    description: 'Ton informel et proche',
  },
];

export const DescriptionPreferencesStep = forwardRef<DescriptionPreferencesStepRef, DescriptionPreferencesStepProps>(
  ({ onComplete, setLoading, onCanProceedChange, onPreferencesChange }, ref) => {
    const { userId } = useAuthStore();
    const planningPreferences = useOnboardingDataStore((state) => state.planningPreferences);
    const updatePlanningPreferences = useOnboardingDataStore((state) => state.updatePlanningPreferences);
    
    // Form state - initialize from preloaded data
    const [toneOfVoice, setToneOfVoice] = useState<ToneOfVoice>(
      planningPreferences?.tone_of_voice || 'vous'
    );
    const [hasRecurringCta, setHasRecurringCta] = useState(
      planningPreferences?.has_recurring_cta ?? false
    );
    const [recurringCtaText, setRecurringCtaText] = useState(
      planningPreferences?.recurring_cta_text || ''
    );
    const [hasEmojis, setHasEmojis] = useState(
      planningPreferences?.has_emojis ?? true
    );

    // Sync with preloaded data when it becomes available
    useEffect(() => {
      if (planningPreferences) {
        if (planningPreferences.tone_of_voice) {
          setToneOfVoice(planningPreferences.tone_of_voice);
        }
        if (planningPreferences.has_recurring_cta !== undefined) {
          setHasRecurringCta(planningPreferences.has_recurring_cta);
        }
        if (planningPreferences.recurring_cta_text) {
          setRecurringCtaText(planningPreferences.recurring_cta_text);
        }
        if (planningPreferences.has_emojis !== undefined) {
          setHasEmojis(planningPreferences.has_emojis);
        }
      }
    }, [planningPreferences]);

    // Notify parent of preference changes for live preview
    useEffect(() => {
      onPreferencesChange?.({
        toneOfVoice,
        hasRecurringCta,
        recurringCtaText,
        hasEmojis,
      });
    }, [toneOfVoice, hasRecurringCta, recurringCtaText, hasEmojis, onPreferencesChange]);

    // Always allow proceeding - tone has a default value
    useEffect(() => {
      onCanProceedChange?.(true);
    }, [onCanProceedChange]);

    const saveAndContinue = useCallback(async () => {
      if (!userId) return;
      
      setLoading(true);
      
      // Optimistic update in store
      updatePlanningPreferences({
        tone_of_voice: toneOfVoice,
        has_recurring_cta: hasRecurringCta,
        recurring_cta_text: hasRecurringCta ? recurringCtaText : null,
        has_emojis: hasEmojis,
      });
      
      const result = await upsertDescriptionPreferences(
        userId,
        toneOfVoice,
        hasRecurringCta,
        hasRecurringCta ? recurringCtaText : null,
        hasEmojis
      );
      
      if (result.success) {
        onComplete();
      }
      
      setLoading(false);
    }, [userId, toneOfVoice, hasRecurringCta, recurringCtaText, hasEmojis, setLoading, onComplete, updatePlanningPreferences]);

    useImperativeHandle(ref, () => ({
      handleContinue: saveAndContinue
    }), [saveAndContinue]);

    return (
      <div className="space-y-6">
        {/* Tone of voice selection */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="h-4 w-4 text-warning" />
            <span className="text-sm font-medium text-white">Ton de voix</span>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {TONE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setToneOfVoice(option.value)}
                className={cn(
                  "relative flex flex-col items-center justify-center p-5 rounded-xl border-2 transition-all",
                  toneOfVoice === option.value
                    ? "border-warning bg-warning/10"
                    : "border-white/10 bg-white/[0.02] hover:border-white/20"
                )}
              >
                {toneOfVoice === option.value && (
                  <div className="absolute top-2.5 right-2.5">
                    <div className="w-5 h-5 rounded-full bg-warning flex items-center justify-center">
                      <Check className="h-3 w-3 text-black" />
                    </div>
                  </div>
                )}
                <span className={cn(
                  "text-2xl font-bold mb-1",
                  toneOfVoice === option.value ? "text-warning" : "text-white"
                )}>
                  {option.label}
                </span>
                <span className="text-xs text-white/50 text-center">
                  {option.description}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Emoji toggle */}
        <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/10">
          <div className="flex items-center gap-3">
            <Smile className="h-4 w-4 text-warning" />
            <div className="space-y-0.5">
              <span className="text-sm font-medium text-white">
                Emojis
              </span>
              <p className="text-xs text-white/50">
                Ajouter des emojis dans les descriptions
              </p>
            </div>
          </div>
          <Switch
            checked={hasEmojis}
            onCheckedChange={setHasEmojis}
            className="data-[state=checked]:bg-warning"
          />
        </div>

        {/* Recurring CTA section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/10">
            <div className="space-y-0.5">
              <span className="text-sm font-medium text-white">
                Call-to-Action récurrent
              </span>
              <p className="text-xs text-white/50">
                Ajouté automatiquement à toutes vos descriptions
              </p>
            </div>
            <Switch
              checked={hasRecurringCta}
              onCheckedChange={setHasRecurringCta}
              className="data-[state=checked]:bg-warning"
            />
          </div>

          {hasRecurringCta && (
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
              <label htmlFor="recurring-cta-textarea" className="text-xs text-white/50">
                Votre phrase de CTA
              </label>
              <Textarea
                id="recurring-cta-textarea"
                value={recurringCtaText}
                onChange={(e) => setRecurringCtaText(e.target.value)}
                placeholder="Ex: Suivez-moi pour plus de conseils ! 🚀"
                className="bg-white/[0.02] border-white/10 text-white placeholder:text-white/30 min-h-[80px] resize-none focus:ring-warning focus:border-warning/50"
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);

DescriptionPreferencesStep.displayName = 'DescriptionPreferencesStep';
